import { useContext, useState } from 'react';
import { Typography, Grid, Box } from '@mui/material';
import { UserContext } from '../contexts/UserContext';
import { getCardStats } from '../utilities';
import { PlayingCard } from './PlayingCard';
import CardInfoDialog from './CardInfoDialog';

export default function DeckOverview({
    showDefense = true,
    showActive = true,
}) {
    const { userData, globalCards } = useContext(UserContext) || {};
    const [selectedCard, setSelectedCard] = useState(null);

    if (!userData || !globalCards) return null;

    const fmt = (n) => Number(n ?? 0).toLocaleString();

    // Hydrate all owned instances (stats + evolution visuals)
    const allCards = (userData.ownedCards || [])
        .map((instance, ownedIndex) => {
            const base = globalCards[instance.cardId];
            if (!base) return null;
            const { attack, defense } = getCardStats(instance, base);
            const evo = base.evolutions?.[instance.evolutionStage] || {};
            return {
                ...base,
                ...instance,
                attack,
                defense,
                image: evo.image || base.image,
                rarity: evo.rarity || base.rarity,
                ability: evo.ability || base.ability,
                abilityDescription: evo.abilityDescription || base.abilityDescription,
                ownedIndex, // stable fallback for tie-breaks
            };
        })
        .filter(Boolean);

    // Shared sorter: order by given field, then by ownedIndex for consistency
    const sortByOrder = (a, b, field) => {
        const ao = typeof a[field] === 'number' ? a[field] : 999;
        const bo = typeof b[field] === 'number' ? b[field] : 999;
        if (ao !== bo) return ao - bo;
        return (a.ownedIndex ?? 0) - (b.ownedIndex ?? 0);
    };

    const defenseCards = allCards
        .filter((c) => c.defenseActive)
        .sort((a, b) => sortByOrder(a, b, 'defenseOrder'));

    const activeCards = allCards
        .filter((c) => c.active)
        .sort((a, b) => sortByOrder(a, b, 'activeOrder'));

    const defenseTotalAttack = defenseCards.reduce((s, c) => s + c.attack, 0);
    const defenseTotalDefense = defenseCards.reduce((s, c) => s + c.defense, 0);
    const activeTotalAttack = activeCards.reduce((s, c) => s + c.attack, 0);
    const activeTotalDefense = activeCards.reduce((s, c) => s + c.defense, 0);

    const renderCard = (card, i, size = 'md') => (
        <Grid item key={`${card.instanceId || card.id || 'card'}-${i}`}>
            <PlayingCard showStats="True" card={card} size={size} onClick={() => setSelectedCard(card)} />
        </Grid>
    );

    // ---------- DeckRow helper (single row of 5; first is 1.5x larger) ----------
    function DeckRow({ cards, onCardClick }) {
        const row = cards.slice(0, 5);
        return (
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
                    gap: 2,
                    alignItems: 'stretch',
                    mb: 4,
                }}
            >
                {row.map((card, i) => (
                    <Box
                        key={card.instanceId || card.id || `card-${i}`}
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'stretch',
                            transform: i === 0 ? 'scale(1)' : 'none',
                            transformOrigin: 'center',
                        }}
                    >
                        <PlayingCard
                            showStats="True"
                            card={card}
                            showLevel="True"
                            size="md"
                            onClick={() => onCardClick?.(card)}
                        />
                    </Box>
                ))}
                {Array.from({ length: Math.max(0, 5 - row.length) }).map((_, idx) => (
                    <Box key={`empty-${idx}`} />
                ))}
            </Box>
        );
    }
    // ---------------------------------------------------------------------------

    return (
        <>
            {/* Defense Deck (conditional) */}
            {showDefense && (
                <>
                    <Typography variant="h5" gutterBottom>Defense Deck</Typography>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body1">
                            <strong>Total Attack:</strong> {fmt(defenseTotalAttack)} | <strong>Total Defense:</strong> {fmt(defenseTotalDefense)}
                        </Typography>
                    </Box>
                    {defenseCards.length === 0 ? (
                        <Typography sx={{ mb: 4 }}>No cards in defense deck.</Typography>
                    ) : (
                        <DeckRow cards={defenseCards} onCardClick={setSelectedCard} />
                    )}
                </>
            )}

            {/* Active Deck (conditional) */}
            {showActive && (
                <>
                    <Typography variant="h5" gutterBottom>Attack Deck</Typography>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body1">
                            <strong>Total Attack:</strong> {fmt(activeTotalAttack)} | <strong>Total Defense:</strong> {fmt(activeTotalDefense)}
                        </Typography>
                    </Box>
                    {activeCards.length === 0 ? (
                        <Typography>No cards in attack deck.</Typography>
                    ) : (
                        <DeckRow cards={activeCards} onCardClick={setSelectedCard} />
                    )}
                </>
            )}

            {/* Info-only dialog */}
            <CardInfoDialog
                open={!!selectedCard}
                onClose={() => setSelectedCard(null)}
                card={selectedCard}
                readOnly  // hides edit buttons
            />
        </>
    );
}
